import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, MapPin, Send } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { submitContactForm } from '../services/api';
import HoverText from './HoverText';

const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await submitContactForm(formData);
      toast({
        title: "Vielen Dank für Ihre Nachricht",
        description: response.message || "Wir melden uns bald bei Ihnen.",
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast({
        title: "Fehler",
        description: "Fehler beim Senden der Nachricht. Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "E-Mail",
      value: "contact@trees-life.charity",
      link: "mailto:contact@trees-life.charity"
    },
    {
      icon: MapPin,
      title: "Adresse",
      value: "Chaltenbodenstrasse 26, CH-8834 Schindellegi",
      link: null
    }
  ];

  return (
    <section id="contact" className="py-16 md:py-24 lg:py-32 bg-gradient-to-b from-gray-50 to-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-left md:text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <HoverText 
            className="text-gray-900" 
            style={{ 
              fontFamily: "'Playfair Display', serif", 
              fontSize: 'clamp(28px, 5vw, 42px)', 
              fontWeight: 700,
              lineHeight: 1.2,
              textAlign: 'left',
              marginBottom: '16px'
            }}
          >
            Wir freuen uns von Ihnen zu hören
          </HoverText>
          <HoverText 
            className="text-gray-600" 
            style={{ 
              fontSize: 'clamp(16px, 3vw, 18px)', 
              fontWeight: 400, 
              color: '#666', 
              lineHeight: 1.6,
              textAlign: 'left',
              marginBottom: '16px'
            }}
          >
            Haben Sie Fragen oder möchten Sie mehr über unsere Arbeit erfahren? Kontaktieren Sie uns!
          </HoverText>
        </div>

        {/* REFACTORED: flex-col auf Mobile, Grid auf Desktop */}
        <div className="flex flex-col lg:grid lg:grid-cols-5 gap-8 lg:gap-12">
          {/* Contact Info - Volle Breite auf Mobile */}
          <div className="w-full lg:col-span-2 space-y-6">
            {contactInfo.map((info, index) => {
              const Icon = info.icon;
              return (
                <div key={index} className="flex items-start gap-4 group">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF1B8D] to-[#FF9B8C] flex items-center justify-center shadow-lg group-hover:shadow-xl group-hover:scale-110 transition-all duration-300">
                    <Icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1 text-left">{info.title}</h3>
                    {info.link ? (
                      <a
                        href={info.link}
                        className="text-gray-600 hover:text-[#FF1B8D] transition-colors duration-300 break-words text-left block"
                      >
                        {info.value}
                      </a>
                    ) : (
                      <p className="text-gray-600 break-words text-left">{info.value}</p>
                    )}
                  </div>
                </div>
              );
            })}

            {/* Additional Info - REFACTORED: flex-col für bessere Lesbarkeit */}
            <div className="mt-8 lg:mt-12 p-6 bg-gradient-to-br from-[#FF1B8D]/5 to-[#FF9B8C]/5 rounded-2xl border-2 border-[#FF9B8C]/20">
              <div className="flex flex-col">
                <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">Gemeinnützige Organisation</h3>
                <p className="text-sm md:text-base text-gray-600 leading-relaxed">
                  Trees Life Charity ist als gemeinnützige Organisation aktiv. Ihre Spende ist steuerlich absetzbar.
                </p>
              </div>
            </div>
          </div>

          {/* Contact Form - Volle Breite auf Mobile */}
          <div className="w-full lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-gray-200">
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4 md:gap-6">
                  <div>
                    <Label htmlFor="contact-name" className="text-sm font-medium text-gray-700 mb-2 block">
                      Name *
                    </Label>
                    <Input
                      id="contact-name"
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12 border-2 focus:border-[#FF1B8D] w-full"
                      placeholder="Ihr Name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="contact-email" className="text-sm font-medium text-gray-700 mb-2 block">
                      E-Mail *
                    </Label>
                    <Input
                      id="contact-email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-12 border-2 focus:border-[#FF1B8D] w-full"
                      placeholder="ihre.email@beispiel.de"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="contact-subject" className="text-sm font-medium text-gray-700 mb-2 block">
                    Betreff *
                  </Label>
                  <Input
                    id="contact-subject"
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="h-12 border-2 focus:border-[#FF1B8D] w-full"
                    placeholder="Worum geht es?"
                  />
                </div>

                <div>
                  <Label htmlFor="contact-message" className="text-sm font-medium text-gray-700 mb-2 block">
                    Nachricht *
                  </Label>
                  <Textarea
                    id="contact-message"
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="min-h-[150px] border-2 focus:border-[#FF1B8D] resize-none w-full"
                    placeholder="Ihre Nachricht an uns..."
                  />
                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-[#FF1B8D] to-[#E01679] hover:from-[#E01679] hover:to-[#C01568] text-white py-5 md:py-6 rounded-full text-base md:text-lg font-bold shadow-xl hover:shadow-2xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="mr-2 h-5 w-5" />
                  {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;